import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Footer from "../../components/Footer";
import { checkToken, resetPasswordRequested } from "../../services/authService";

// Icons as inline SVG (for simplicity instead of Material UI)
const VisibilityIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const VisibilityOffIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
  </svg>
);

const SpinnerIcon = () => (
  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const CrossIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get token from URL query parameters
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get('token');
  
  // States
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formTouched, setFormTouched] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  
  // Environment variable for backend URL
  const backendURL = import.meta.env.VITE_API_URL;
  
  // Password requirements validation
  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });

  // Validate token when component mounts
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('Token de restablecimiento inválido o faltante');
        setValidatingToken(false);
        return;
      }

      try {
        const response = await checkToken(token);

        if (response.message === 'Token válido') {
          setTokenValid(true);
        } else {
          setError('El enlace de restablecimiento es inválido o ha expirado');
        }
      } catch (error) {
        console.error('Token validation error:', error);
        setError('Error al validar el enlace de restablecimiento');
      } finally {
        setValidatingToken(false);
      }
    };

    validateToken();
  }, [token, backendURL]);

  // Validate password requirements as user types
  useEffect(() => {
    if (password) {
      setPasswordRequirements({
        length: password.length >= 8,
        uppercase: /[A-Z]/.test(password),
        lowercase: /[a-z]/.test(password),
        number: /[0-9]/.test(password),
        special: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)
      });
    } else {
      setPasswordRequirements({
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false
      });
    }
  }, [password]);

  // Check if passwords match and all requirements are met
  const passwordsMatch = password === confirmPassword && confirmPassword !== '';
  const allRequirementsMet = Object.values(passwordRequirements).every(req => req === true);
  const isFormValid = passwordsMatch && allRequirementsMet;

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Handle password input
  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (!formTouched) {
      setFormTouched(true);
    }
  };

  // Handle confirm password input
  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    if (!formTouched) {
      setFormTouched(true);
    }
  };

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Clear previous messages
    setError('');
    setSuccess('');
    
    // Form validation
    if (!isFormValid) {
      setError('Por favor, asegúrese de cumplir con todos los requisitos de contraseña');
      return;
    }
    
    setLoading(true);

    try {
      const response = await resetPasswordRequested({
        token,
        password,
        passwordConfirmation: confirmPassword
      });
      if (response.message === 'Contraseña actualizada correctamente') {
        setSuccess('Su contraseña ha sido restablecida exitosamente');
        
        // Clear form fields after successful submission
        setPassword('');
        setConfirmPassword('');
        setFormTouched(false);
        
        // Redirect to login page after 3 seconds
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else {
        setError(response.message || 'Error al restablecer la contraseña');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      setError('Error de conexión. Por favor, intente más tarde.');
    } finally {
      setLoading(false);
    }
  };

  // Show loading indicator while validating token
  if (validatingToken) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-customBlue"></div>
          <p className="mt-4 text-customBlue font-semibold">Validando enlace...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col justify-between font-sans bg-cover bg-center bg-no-repeat"
      style={{
        backgroundImage: "url('/Gráficas web RDS-02.jpg')",
      }}
    >
      <div className="flex flex-1 justify-center items-center p-4 md:flex-row md:py-10 lg:px-20">
        <div className="flex flex-col items-center justify-center p-6 rounded-lg w-full max-w-lg md:max-w-md lg:max-w-xl">
          <div className="bg-white p-8 rounded-lg shadow-xl w-full transition-all duration-300 ease-in-out">
            <div className="flex justify-center mb-6">
              <img 
                src="/RDS - LOGO VERTICAL-01.png" 
                alt="Logo RDS" 
                className="h-32 mx-auto" 
                style={{ filter: 'drop-shadow(0px 2px 3px rgba(0,0,0,0.1))' }}
              />
            </div>
            
            <h1 className="text-customBlue text-2xl md:text-3xl font-bold text-center mb-8">
              Restablecer contraseña
            </h1>

            {!tokenValid ? (
              <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded mb-4">
                <p className="text-center" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  {error || 'El enlace es inválido o ha expirado'}
                </p>
                <div className="mt-4 text-center">
                  <button
                    onClick={() => navigate('/login')}
                    className="bg-customRed text-white py-2 px-4 rounded hover:bg-customRedLight transition-colors"
                  >
                    Volver al inicio de sesión
                  </button>
                </div>
              </div>
            ) : (
              <form className="w-full max-w-md mx-auto" onSubmit={handleSubmit}>
                {/* Password Field */}
                <div className="mb-4">
                  <label
                    className="block text-customBlue font-bold mb-1"
                    htmlFor="password"
                    style={{ fontFamily: 'Nunito, sans-serif' }}
                  >
                    Nueva contraseña <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      placeholder="Ingrese su nueva contraseña"
                      value={password}
                      onChange={handlePasswordChange}
                      className="w-full border border-customBlue px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-customBlueLight"
                      required
                    />
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="absolute right-2 top-2 text-gray-600 hover:text-gray-800 focus:outline-none"
                      aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </button>
                  </div>
                </div>

                {/* Password Requirements List */}
                <div className="mb-4 bg-gray-50 p-3 rounded-md border border-gray-200">
                  <p className="text-sm font-semibold mb-2 text-gray-700">
                    La contraseña debe cumplir con:
                  </p>
                  <ul className="text-xs space-y-1">
                    <li className="flex items-center">
                      <span className={`w-5 ${passwordRequirements.length ? 'text-green-500' : 'text-red-500'}`}>
                        {passwordRequirements.length ? <CheckIcon /> : <CrossIcon />}
                      </span>
                      <span className={passwordRequirements.length ? 'text-green-700' : 'text-gray-700'}>
                        Al menos 8 caracteres
                      </span>
                    </li>
                    <li className="flex items-center">
                      <span className={`w-5 ${passwordRequirements.uppercase ? 'text-green-500' : 'text-red-500'}`}>
                        {passwordRequirements.uppercase ? <CheckIcon /> : <CrossIcon />}
                      </span>
                      <span className={passwordRequirements.uppercase ? 'text-green-700' : 'text-gray-700'}>
                        Al menos una letra mayúscula (A-Z)
                      </span>
                    </li>
                    <li className="flex items-center">
                      <span className={`w-5 ${passwordRequirements.lowercase ? 'text-green-500' : 'text-red-500'}`}>
                        {passwordRequirements.lowercase ? <CheckIcon /> : <CrossIcon />}
                      </span>
                      <span className={passwordRequirements.lowercase ? 'text-green-700' : 'text-gray-700'}>
                        Al menos una letra minúscula (a-z)
                      </span>
                    </li>
                    <li className="flex items-center">
                      <span className={`w-5 ${passwordRequirements.number ? 'text-green-500' : 'text-red-500'}`}>
                        {passwordRequirements.number ? <CheckIcon /> : <CrossIcon />}
                      </span>
                      <span className={passwordRequirements.number ? 'text-green-700' : 'text-gray-700'}>
                        Al menos un número (0-9)
                      </span>
                    </li>
                    <li className="flex items-center">
                      <span className={`w-5 ${passwordRequirements.special ? 'text-green-500' : 'text-red-500'}`}>
                        {passwordRequirements.special ? <CheckIcon /> : <CrossIcon />}
                      </span>
                      <span className={passwordRequirements.special ? 'text-green-700' : 'text-gray-700'}>
                        Al menos un carácter especial (!@#$%^&*()_+-=[]etc.)
                      </span>
                    </li>
                  </ul>
                </div>

                {/* Confirm Password Field */}
                <div className="mb-6">
                  <label
                    className="block text-customBlue font-bold mb-1"
                    htmlFor="confirmPassword"
                    style={{ fontFamily: 'Nunito, sans-serif' }}
                  >
                    Confirmar contraseña <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirmPassword"
                      placeholder="Confirme su nueva contraseña"
                      value={confirmPassword}
                      onChange={handleConfirmPasswordChange}
                      className={`w-full border px-3 py-2 rounded-md focus:outline-none focus:ring-2 
                        ${formTouched && confirmPassword && !passwordsMatch ? 
                          'border-red-500 focus:ring-red-300' : 
                          'border-customBlue focus:ring-customBlueLight'}
                        transition-all duration-200`}
                      required
                    />
                    <button
                      type="button"
                      onClick={toggleConfirmPasswordVisibility}
                      className="absolute right-2 top-2 text-gray-600 hover:text-gray-800 focus:outline-none"
                      aria-label={showConfirmPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    >
                      {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </button>
                  </div>
                  {formTouched && confirmPassword && !passwordsMatch && (
                    <p className="text-red-500 text-xs mt-1">Las contraseñas no coinciden</p>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className={`w-full text-white py-2 rounded-md flex justify-center items-center transition-all duration-200 focus:outline-none
                    ${isFormValid ? 
                      'bg-customRed hover:bg-customRedLight active:bg-customRed' : 
                      'bg-gray-400 cursor-not-allowed'}
                  `}
                  style={{ fontFamily: 'Nunito, sans-serif' }}
                  disabled={loading || !isFormValid}
                >
                  {loading ? <SpinnerIcon /> : 'Restablecer contraseña'}
                </button>

                {/* Back to Login Link */}
                <div className="mt-4 text-center">
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="hover:underline text-customBlue focus:outline-none focus:ring-2 focus:ring-customBlueLight rounded px-2 py-1"
                    style={{ fontFamily: 'Nunito, sans-serif' }}
                  >
                    Volver al inicio de sesión
                  </button>
                </div>

                {/* Error and Success Messages */}
                {error && (
                  <div className="mt-4 p-2 bg-red-100 border border-red-400 text-red-700 rounded" role="alert">
                    <p className="text-center text-sm" style={{ fontFamily: 'Nunito, sans-serif' }}>
                      {error}
                    </p>
                  </div>
                )}
                
                {success && (
                  <div className="mt-4 p-2 bg-green-100 border border-green-400 text-green-700 rounded" role="alert">
                    <p className="text-center text-sm" style={{ fontFamily: 'Nunito, sans-serif' }}>
                      {success}
                    </p>
                  </div>
                )}
              </form>
            )}
            
            <div className="mt-6 text-center text-gray-500 text-sm">
              <p>© {new Date().getFullYear()} RDS. Todos los derechos reservados.</p>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ResetPasswordPage;